import { useMemo } from "react";
import useCrmAssignments from "./useCrmAssignments";
import type { CrmHandlerView } from "../../components/_dashboard/crm/handler/crmHandlerFace";
import type { REST_schema } from "../../@types/sharedSchema";

/**
 * ONE PERSON'S מתווך מטפל — for the surfaces that are not a list.
 *
 * The new-contact modal (which has just one phone in hand), the side panel opened by a deep
 * link (which has no row), the signature form's chosen signer, and a calendar attendee card.
 *
 * A thin wrapper over {@link useCrmAssignments} rather than a second fetcher, so all of them
 * share the one module cache: on a warm CRM page these issue NOTHING, because the list has
 * already answered for every phone on screen.
 *
 * `contactId` may be null while the user is still typing a phone — the hook then asks about
 * nothing, which is what keeps the new-contact modal from issuing a request per keystroke
 * (its caller debounces the phone before handing it over).
 */
export default function useContactHandler(
  contactId: string | null | undefined,
  options: { orgId?: string; enabled?: boolean } = {}
): {
  /** `undefined` = not answered / no agency. `null` = answered, nobody designated. */
  handler: CrmHandlerView | null | undefined;
  setLocal: (assignment: REST_schema.CrmContactAssignment | null) => void;
} {
  const ids = useMemo(() => (contactId ? [contactId] : []), [contactId]);
  const { handlerFor, setLocal } = useCrmAssignments({ contactIds: ids, orgId: options.orgId, enabled: options.enabled });

  return {
    handler: contactId ? handlerFor(contactId) : undefined,
    setLocal: (assignment) => {
      if (contactId) setLocal(contactId, assignment);
    }
  };
}

/**
 * The same answer for a SMALL, bounded set — the contact picker's visible options.
 *
 * ⚠️ THE CALLER MUST CAP THE LIST. `NewContactPicker`'s options are the realtor's whole book
 * (up to ~25,000 rows), and the endpoint takes 200 ids. The picker therefore passes the
 * FILTERED head of its option list, capped, and re-asks as the input narrows — the module
 * cache means each phone is asked about once per session however many times it reappears.
 */
export function useContactHandlers(
  contactIds: readonly string[],
  options: { orgId?: string; enabled?: boolean } = {}
): { handlerFor: (contactId: string) => CrmHandlerView | null | undefined } {
  const { handlerFor } = useCrmAssignments({ contactIds, orgId: options.orgId, enabled: options.enabled });
  return { handlerFor };
}
